// components/playground/sections/ManageHiddenItemsModal.jsx
import React, { useMemo } from 'react';
import { XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import Modal from '../../../common/Modal';

export const ManageHiddenItemsModal = ({ 
  isOpen, 
  onClose, 
  hiddenItems,
  lineItems,
  onRestoreItem 
}) => {
  // Get details for hidden items
  const hiddenItemsDetails = useMemo(() => {
    return hiddenItems
      .map(accountId => {
        const item = lineItems.find(li => li.account_id === accountId);
        return item ? {
          account_id: accountId,
          account_name: item.account_name,
          fully_qualified_name: item.fully_qualified_name,
          classification: item.classification
        } : null;
      })
      .filter(Boolean);
  }, [hiddenItems, lineItems]);

  // Group by classification
  const groupedItems = useMemo(() => {
    const groups = {};
    hiddenItemsDetails.forEach(item => {
      const classification = item.classification || 'Other';
      if (!groups[classification]) {
        groups[classification] = [];
      }
      groups[classification].push(item);
    });
    return groups;
  }, [hiddenItemsDetails]);

  const footer = (
    <button
      onClick={onClose}
      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
    >
      Close
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Hidden Items"
      subtitle={`${hiddenItems.length} item${hiddenItems.length !== 1 ? 's' : ''} currently hidden`}
      footer={footer}
      size="lg"
    >
      {hiddenItems.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <EyeIcon className="w-16 h-16 mx-auto" />
          </div>
          <p className="text-gray-600 text-lg font-medium">No hidden items</p>
          <p className="text-gray-500 text-sm mt-2">
            All line items are currently visible in the table
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            These items are hidden from the table view but still included in all calculations and totals.
          </p>

          {Object.entries(groupedItems).map(([classification, items]) => (
            <div key={classification} className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {classification}
              </h3>
              <div className="space-y-2">
                {items.map(item => (
                  <div
                    key={item.account_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.account_name}
                      </p>
                      {item.fully_qualified_name !== item.account_name && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {item.fully_qualified_name}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => onRestoreItem(item.account_id)}
                      className="ml-4 flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                    >
                      <EyeIcon className="w-4 h-4" />
                      Show
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};